import { Analytics } from 'hooks/useAnalytics'
import { channelIdFromPathname } from 'hooks/useChannelIdFromPathname'
import { spaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { SpaceAddressFromSpaceId } from 'use-towns-client'

import { getCachedSpaceDataForAnalytics } from './useGatherSpaceDetailsAnalytics'

const trackedMessageFailures = new Set<string>()

const getPageType = (): string | undefined => {
    const pathname = typeof location !== 'undefined' ? location.pathname : ''
    if (!pathname) {
        return undefined
    }
    const spaceId = spaceIdFromPathname(pathname)
    const spaceAddress = spaceId ? SpaceAddressFromSpaceId(spaceId) : undefined
    let type: string | undefined
    if (spaceAddress) {
        const parts = pathname.toLowerCase().split(`/${spaceAddress.toLowerCase()}/`)
        const subPath = parts[1] ?? ''
        type = subPath.split('/')[0] || undefined
    } else {
        const segments = pathname.split('/')
        type = segments[1] || undefined
    }
    return type
}

export function appLoadedEvent() {
    // Analytics.getInstance().track('app loaded')
}

const cachedDataWithSpaceAndChannelId = (): ReturnType<typeof getCachedSpaceDataForAnalytics> & {
    spaceId: string | undefined
    channelId: string | undefined
} => {
    const data = getCachedSpaceDataForAnalytics()
    return {
        ...data,
        spaceId: spaceIdFromPathname(location.pathname),
        channelId: channelIdFromPathname(location.pathname),
    }
}

//////////////////////////////////////
/////// search events ///////////////
//////////////////////////////////////
export const enteredSearchTerm = (args: { termLength: number }) => {
    Analytics.getInstance().track('entered search term', {
        ...cachedDataWithSpaceAndChannelId(),
        ...args,
    })
}

export const clickedOnSearchResult = (args: {
    resultType:
        | 'member'
        | 'direct message'
        | 'channel message'
        | 'action'
        | 'dm channel'
        | 'channel'
        | 'space'
}) => {
    Analytics.getInstance().track('click search result', {
        ...cachedDataWithSpaceAndChannelId(),
        ...args,
    })
}
//////////////////////////////////////
//////////////////////////////////////

//////////////////////////////////////
/////// mention events //////
//////////////////////////////////////
export const mentionPrompt = () => {
    Analytics.getInstance().track('mention prompt', {
        ...cachedDataWithSpaceAndChannelId(),
    })
}
export const clickedMentionSuggestion = () => {
    Analytics.getInstance().track('click mention suggestion', {
        ...cachedDataWithSpaceAndChannelId(),
    })
}
//////////////////////////////////////
//////////////////////////////////////

//////////////////////////////////////
/////// loading content events //////
//////////////////////////////////////
// only call this once per session on the first space load
export const loadedAppSkeleton = () => {
    // Analytics.getInstance().track('loaded app skeleton', {
    //     pageType: getPageType(),
    //     ...cachedDataWithSpaceAndChannelId(),
    // })
}
// this isn't really checking cached content
// instead the way it was discussed is, we want to know when the user has some useful content in front of them that is not the loading screen
export const loadedFromCache = () => {
    // Analytics.getInstance().track('loaded from cache', {
    //     pageType: getPageType(),
    //     ...cachedDataWithSpaceAndChannelId(),
    // })
}
//////////////////////////////////////
//////////////////////////////////////

//////////////////////////////////////
/////// message failure events //////
//////////////////////////////////////
export const postedMessageFailed = (args: { eventId: string }) => {
    if (trackedMessageFailures.has(args.eventId)) {
        return
    }
    trackedMessageFailures.add(args.eventId)
    Analytics.getInstance().track('posted message failed', {
        pageType: getPageType(),
        ...cachedDataWithSpaceAndChannelId(),
    })
}

export const postedMessageRetry = () => {
    Analytics.getInstance().track('posted message retry', {
        pageType: getPageType(),
        ...cachedDataWithSpaceAndChannelId(),
    })
}
//////////////////////////////////////
//////////////////////////////////////

//////////////////////////////////////
/////// encrypted message body events //////
//////////////////////////////////////
export const messagesEncrypted = (args: { encryptedCount: number }) => {
    Analytics.getInstance().track('messages encrypted', {
        pageType: getPageType(),
        ...cachedDataWithSpaceAndChannelId(),
        ...args,
    })
}
export const messagesDecrypted = () => {
    Analytics.getInstance().track('messages decrypted', {
        pageType: getPageType(),
        ...cachedDataWithSpaceAndChannelId(),
    })
}
//////////////////////////////////////
//////////////////////////////////////

//////////////////////////////////////
/////// sync events ///////////////
//////////////////////////////////////
export const syncStart = (ldFlags: string | undefined) => {
    Analytics.getInstance().track('sync start', {
        pageType: getPageType(),
        ...cachedDataWithSpaceAndChannelId(),
        activeFlags: ldFlags,
        ...getMemoryMetrics(),
    })
}
export const initialSyncComplete = (details: {
    syncDuration: number
    syncedStreamsCount: number
    ldFlags: string | undefined
}) => {
    // const { ldFlags, ...rest } = details
    // Analytics.getInstance().track('initial sync complete', {
    //     pageType: getPageType(),
    //     ...cachedDataWithSpaceAndChannelId(),
    //     ...rest,
    //     activeFlags: ldFlags,
    //     ...getMemoryMetrics(),
    // })
}
export const channelVisible = (channelId: string) => {
    // Analytics.getInstance().track('channel visible', {
    //     pageType: getPageType(),
    //     ...cachedDataWithSpaceAndChannelId(),
    //     channelId,
    // })
}
export const channelSyncComplete = (channelId: string, ldFlags: string | undefined) => {
    Analytics.getInstance().track('channel sync complete', {
        pageType: getPageType(),
        ...cachedDataWithSpaceAndChannelId(),
        channelId,
        activeFlags: ldFlags,
        ...getMemoryMetrics(),
    })
}

function getMemoryMetrics() {
    let memoryUtilization = null
    let memoryUsedMB = null

    const performanceMemory = window.performance?.memory
    if (performanceMemory) {
        const usedJSHeapSize = performanceMemory.usedJSHeapSize
        const jsHeapSizeLimit = performanceMemory.jsHeapSizeLimit
        memoryUsedMB = (usedJSHeapSize / (1024 * 1024)).toFixed(2)
        memoryUtilization = ((usedJSHeapSize / jsHeapSizeLimit) * 100).toFixed(2)
    }

    return {
        memory_utilization_percent: memoryUtilization,
        used_js_heap_size_mb: memoryUsedMB,
    }
}
